import React, { useEffect, useState } from 'react';
import { Modal, ModalBody } from 'reactstrap';
import { Bell, Camera, Video, MapPin, Clock, Cpu, CheckCircle, X, ZoomIn } from 'react-feather';
import notificationService from '../../../api/notificationService';
import ImageZoom from '../../../Components/Dashboards/AreaDashbaord/reports/Components/LiveAlertsCards/ImageZoom';
import './BellNotification.css';

const severityKeyMap = { high: 'high', medium: 'medium', low: 'low' };
const getSeverityKey = (severity) => severityKeyMap[severity?.toLowerCase()] || 'default';

const formatLabel = (value) => {
  if (!value) return '';
  return value
    .split('_')
    .filter(Boolean)
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');
};

const formatDayLabel = (dateStr) => {
  if (!dateStr) return '';
  const target = new Date(dateStr);
  if (isNaN(target.getTime())) return dateStr;

  const stripTime = (d) => {
    const copy = new Date(d);
    copy.setHours(0, 0, 0, 0);
    return copy;
  };

  const diffDays = Math.round((stripTime(new Date()) - stripTime(target)) / 86400000);
  if (diffDays === 0) return 'Today';
  if (diffDays === 1) return 'Yesterday';
  if (diffDays > 1 && diffDays < 7) return target.toLocaleDateString('en-US', { weekday: 'long' });
  return target.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
};

const BellNotification = () => {
  const [notificationDropDown, setNotificationDropDown] = useState(false);
  const [notifications, setNotifications] = useState([]);
  const [unseenCount, setUnseenCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const [selectedAlert, setSelectedAlert] = useState(null);
  const [zoomOpen, setZoomOpen] = useState(false);

  const getNotifications = async (silent = false) => {
    const userData = JSON.parse(localStorage.getItem('userData'));
    if (!userData?.id || !userData?.factory?.id) return;

    if (!silent) setLoading(true);
    try {
      const role = JSON.parse(localStorage.getItem('role'));
      const area_id = userData?.area_ids?.name;
      const payload = {
        user_id: userData.id,
        factory_id: userData.factory.id,
        area: role === 'area' ? area_id : '',
        limit: 6,
      };
      const res = await notificationService.get_notifications(payload);
      if (res.status === 200 && res.data?.success) {
        setNotifications(res.data?.notifications || []);
        setUnseenCount(res.data?.unseen_count || 0);
      }
    } catch (error) {
      console.log(error);
    } finally {
      if (!silent) setLoading(false);
    }
  };

  useEffect(() => {
    getNotifications();
  }, []);

  const handleAlertClick = async (item) => {
    setSelectedAlert(item);

    const userData = JSON.parse(localStorage.getItem('userData'));
    if (!userData?.id) return;

    try {
      await notificationService.mark_seen({
        user_id: userData.id,
        operation_safety_ids: [item.operation_safety_id],
      });
      getNotifications(true);
    } catch (error) {
      console.log(error);
    }
  };

  return (
    <li className='onhover-dropdown'>
      <div className='notification-box' onClick={() => setNotificationDropDown(!notificationDropDown)}>
        <Bell />
        {unseenCount > 0 && (
          <>
            <span className='ai-bell-ring' />
            <span className='ai-bell-badge'>{unseenCount > 9 ? '9+' : unseenCount}</span>
          </>
        )}
      </div>

      <div className={`notification-dropdown ai-alerts-panel onhover-show-div ${notificationDropDown ? 'active' : ''}`}>
        <div className='ai-alerts-header'>
          <div className='ai-alerts-title-group'>
            <p className='ai-alerts-title'>
              <span className='ai-alerts-live-dot' />
              AI Safety Alerts
            </p>
            <span className='ai-alerts-subtitle'>Real-time violation detection</span>
          </div>
          {unseenCount > 0 && (
            <span className='ai-alerts-count-chip'>{unseenCount > 99 ? '99+' : unseenCount} NEW THIS WEEK</span>
          )}
        </div>

        {loading && (
          <ul className='ai-alerts-list'>
            {[0, 1, 2].map((i) => (
              <li key={i} className='ai-alert-item'>
                <div className='ai-alert-skeleton'>
                  <div className='ai-skel-block ai-skel-thumb' />
                  <div className='ai-skel-lines'>
                    <div className='ai-skel-block ai-skel-line' style={{ width: '70%' }} />
                    <div className='ai-skel-block ai-skel-line' style={{ width: '45%' }} />
                  </div>
                </div>
              </li>
            ))}
          </ul>
        )}

        {!loading && notifications.length === 0 && (
          <div className='ai-alerts-empty'>
            <CheckCircle size={30} />
            <div className='ai-alerts-empty-title'>All Clear</div>
            <div className='ai-alerts-empty-sub'>No violations detected recently</div>
          </div>
        )}

        {!loading && notifications.length > 0 && (
          <ul className='ai-alerts-list'>
            {notifications.map((item) => {
              const sevKey = getSeverityKey(item.severity);
              return (
                <li key={item.operation_safety_id} className='ai-alert-item'>
                  <div
                    className={`ai-alert-card severity-${sevKey}`}
                    onClick={() => handleAlertClick(item)}
                  >
                    <div className='ai-alert-thumb'>
                      {item.image ? (
                        <img src={item.image} alt='' />
                      ) : (
                        <div className='ai-alert-thumb-fallback'>
                          <Camera size={16} />
                        </div>
                      )}
                      <span className='ai-alert-ai-chip' title='AI detected'>
                        <Cpu size={9} />
                      </span>
                    </div>

                    <div className='ai-alert-body'>
                      <div className='ai-alert-top-row'>
                        <span className='ai-alert-name'>{formatLabel(item.violation || item.module)}</span>
                        <span className={`ai-severity-pill severity-${sevKey}`}>{item.severity}</span>
                      </div>

                      <div className='ai-alert-meta-row'>
                        <Video size={11} />
                        <span>{item.camera_name || item.camera_id}</span>
                        {(item.area || item.subarea) && (
                          <>
                            <span className='ai-alert-meta-sep'>/</span>
                            <MapPin size={11} />
                            <span>{[item.area, item.subarea].filter(Boolean).join(' · ')}</span>
                          </>
                        )}
                      </div>

                      {item.detail && <div className='ai-alert-detail'>{item.detail}</div>}

                      <div className='ai-alert-bottom-row'>
                        <span className='ai-alert-id'>REF #{item.operation_safety_id}</span>
                        <span className='ai-alert-time'>
                          <Clock size={10} />
                          {formatDayLabel(item.date)} · {item.time}
                        </span>
                      </div>
                    </div>
                  </div>
                </li>
              );
            })}
          </ul>
        )}

        {!loading && notifications.length > 0 && (
          <div className='ai-alerts-footer'>Showing {notifications.length} most recent</div>
        )}
      </div>

      {selectedAlert && (
        <Modal isOpen toggle={() => setSelectedAlert(null)} centered className='ai-detail-modal-wrap'>
          <ModalBody className='ai-detail-modal-body'>
            <button
              type='button'
              className='ai-detail-close'
              onClick={() => setSelectedAlert(null)}
              aria-label='Close'
            >
              <X size={16} />
            </button>

            <div
              className='ai-detail-image-wrap'
              onClick={() => selectedAlert.image && setZoomOpen(true)}
              style={{ cursor: selectedAlert.image ? 'zoom-in' : 'default' }}
            >
              {selectedAlert.image ? (
                <>
                  <img src={selectedAlert.image} alt='' />
                  <div className='ai-detail-image-hint'>
                    <ZoomIn size={13} /> Click to enlarge
                  </div>
                </>
              ) : (
                <div className='ai-detail-image-fallback'>
                  <Camera size={28} />
                </div>
              )}
              <span className={`ai-detail-image-severity severity-${getSeverityKey(selectedAlert.severity)}`}>
                {selectedAlert.severity}
              </span>
            </div>

            <div className='ai-detail-top'>
              <span className='ai-detail-title'>{formatLabel(selectedAlert.violation || selectedAlert.module)}</span>
              <span className='ai-detail-ref'>REF #{selectedAlert.operation_safety_id}</span>
            </div>

            <div className='ai-detail-grid'>
              {selectedAlert.module && (
                <div className='ai-detail-row'>
                  <span className='ai-detail-label'>Module</span>
                  <span className='ai-detail-value'>{formatLabel(selectedAlert.module)}</span>
                </div>
              )}
              {(selectedAlert.camera_name || selectedAlert.camera_id) && (
                <div className='ai-detail-row'>
                  <span className='ai-detail-label'>Camera</span>
                  <span className='ai-detail-value'>
                    {selectedAlert.camera_name}
                    {selectedAlert.camera_id ? ` (${selectedAlert.camera_id})` : ''}
                  </span>
                </div>
              )}
              {selectedAlert.area && (
                <div className='ai-detail-row'>
                  <span className='ai-detail-label'>Area</span>
                  <span className='ai-detail-value'>{selectedAlert.area}</span>
                </div>
              )}
              {selectedAlert.subarea && (
                <div className='ai-detail-row'>
                  <span className='ai-detail-label'>Subarea</span>
                  <span className='ai-detail-value'>{selectedAlert.subarea}</span>
                </div>
              )}
              <div className='ai-detail-row'>
                <span className='ai-detail-label'>Detected</span>
                <span className='ai-detail-value'>
                  {formatDayLabel(selectedAlert.date)} · {selectedAlert.time}
                </span>
              </div>
            </div>

            {selectedAlert.detail && <div className='ai-detail-note'>{selectedAlert.detail}</div>}
          </ModalBody>
        </Modal>
      )}

      {zoomOpen && selectedAlert && (
        <ImageZoom
          photo={selectedAlert.image}
          setIsOpen={setZoomOpen}
          setShowModal={setZoomOpen}
          imageData={{
            cameraName: selectedAlert.camera_name,
            violation: formatLabel(selectedAlert.violation || selectedAlert.module),
            date: formatDayLabel(selectedAlert.date),
            time: selectedAlert.time,
          }}
        />
      )}
    </li>
  );
};

export default BellNotification;
